import isEmpty from 'lodash/isEmpty'
import React from 'react'
import { BusyButton } from '@ztechapps/webui.button'
import { useError, useToast } from '@ztechapps/common.hook'
import { useGenerateBlogTags } from '../hooks/useGenerateBlogTags'

export type GenerateTagsButtonProps = {
  blogId: string
  onTagsGenerated: (tags: string[]) => void
  disabled?: boolean
}

/**
 * Button component for generating AI-powered blog tags
 *
 * @description
 * Displays a button that triggers AI tag generation for a blog post.
 * Generated tags are directly applied without confirmation.
 *
 * @example
 * ```tsx
 * <GenerateTagsButton
 *   blogId="blog-123"
 *   onTagsGenerated={(tags) => setBlogTags(tags)}
 * />
 * ```
 */
export const GenerateTagsButton = ({ blogId, onTagsGenerated, disabled = false }: GenerateTagsButtonProps) => {
  const [generateTags, , { loading }] = useGenerateBlogTags()
  const { showSuccess, showError } = useToast()
  const { onError } = useError()

  const handleGenerateTags = async () => {
    try {
      const result = await generateTags({ input: { blogId } })
      const tags = result?.data?.generateBlogTags ?? []

      if (isEmpty(tags)) {
        showError({ message: 'No tags could be generated for this blog post.' })
        return
      }

      onTagsGenerated(tags)
      showSuccess({ message: 'Tags generated and applied successfully' })
    } catch (err) {
      onError(err instanceof Error ? err : new Error(String(err)))
    }
  }

  return (
    <BusyButton
      icon="wand-magic-sparkles"
      text="Generate Tags"
      onClick={handleGenerateTags}
      isBusy={loading}
      busyText="Generating..."
      disabled={disabled}
      title="Generate AI-powered tags for this blog post"
    />
  )
}
